// Main Admin API service - aggregates all API modules
import { BaseApiClient } from './api/BaseApiClient';
import { UserManagementApi } from './api/UserManagementApi';
import { AnalyticsApi } from './api/AnalyticsApi';
import { BillingPaymentApi } from './api/BillingPaymentApi';
import { AdminAuthApi } from './api/AdminAuthApi';
import { QuotaTierApi } from './api/QuotaTierApi';
import { EmailNotificationApi } from './api/EmailNotificationApi';
import { OrganizationApi } from './api/OrganizationApi';
import { ForumApi } from './api/ForumApi';
import { configService } from './config';

/**
 * Unified Admin API Service
 * 
 * This service aggregates all admin API modules into a single interface.
 * Each module is responsible for a specific domain of functionality:
 * 
 * - UserManagementApi: User CRUD operations, actions, exports
 * - AnalyticsApi: Dashboard metrics, usage analytics, model metrics
 * - BillingPaymentApi: Payments, billing, credits, refunds
 * - AdminAuthApi: Admin users, authentication, roles, audit logs
 * - QuotaTierApi: Quota configuration, tier management
 * - EmailNotificationApi: Email campaigns, notifications, records
 * - OrganizationApi: Team/organization management
 * - ForumApi: Forum moderation, posts, replies, activity
 * 
 * Usage:
 * ```typescript
 * import { adminApiService } from '@/services/adminApi';
 * 
 * // Access user management methods
 * const users = await adminApiService.getUsers();
 * 
 * // Access analytics methods
 * const metrics = await adminApiService.getDashboardMetrics();
 * 
 * // Access billing methods
 * const snapshot = await adminApiService.getBillingSnapshot(userId);
 * ```
 */
class AdminApiService extends BaseApiClient {
  // Domain-specific API modules
  // NOTE: These are initialized as class fields (not in the constructor) because
  // we use class-field delegates like `getUsers = this.userApi.getUsers.bind(...)`.
  // With `useDefineForClassFields: true`, field initializers run before the
  // constructor body, so we must ensure these exist before delegates are evaluated.
  private userApi = new UserManagementApi();
  private analyticsApi = new AnalyticsApi();
  private billingApi = new BillingPaymentApi();
  private adminAuthApi = new AdminAuthApi();
  private quotaTierApi = new QuotaTierApi();
  private emailApi = new EmailNotificationApi();
  private organizationApi = new OrganizationApi();
  private forumApi = new ForumApi();

  constructor() {
    super('AdminAPI');
    this.validateConfiguration();
  }

  /**
   * Validate API configuration on initialization
   */
  private validateConfiguration(): void {
    if (!this.baseURL) {
      this.logger.error('No API base URL configured');
      throw new Error('VITE_API_BASE_URL environment variable is required');
    }

    // Validate required configuration for production
    if (configService.isProduction) {
      if (!configService.googleClientId) {
        this.logger.error('Google Client ID required for production');
        throw new Error('VITE_GOOGLE_CLIENT_ID is required for production');
      }

      if (!configService.adminAuthDomain) {
        this.logger.error('Admin auth domain required for production');
        throw new Error('VITE_ADMIN_AUTH_DOMAIN is required for production');
      }
    }

    // Log initialization
    this.logger.info('Service initialized', {
      baseURL: this.baseURL,
      environment: configService.environment,
      version: configService.appVersion,
      timeout: 30000
    });
  }

  // ============================================================================
  // User Management API - Delegate to UserManagementApi
  // ============================================================================
  
  getUsers = this.userApi.getUsers.bind(this.userApi);
  getUserLanguageStats = this.userApi.getUserLanguageStats.bind(this.userApi);
  getUserStats = this.userApi.getUserStats.bind(this.userApi);
  getUser = this.userApi.getUser.bind(this.userApi);
  updateUser = this.userApi.updateUser.bind(this.userApi);
  performUserAction = this.userApi.performUserAction.bind(this.userApi);
  exportUsers = this.userApi.exportUsers.bind(this.userApi);
  deleteUser = this.userApi.deleteUser.bind(this.userApi);
  getUserLibrary = this.userApi.getUserLibrary.bind(this.userApi);
  getUserPaymentHistory = this.userApi.getUserPaymentHistory.bind(this.userApi);
  getUserUsageStats = this.userApi.getUserUsageStats.bind(this.userApi);

  // ============================================================================
  // Analytics API - Delegate to AnalyticsApi
  // ============================================================================
  
  getDashboardMetrics = this.analyticsApi.getDashboardMetrics.bind(this.analyticsApi);
  getUsageAnalytics = this.analyticsApi.getUsageAnalytics.bind(this.analyticsApi);
  getModelMetrics = this.analyticsApi.getModelMetrics.bind(this.analyticsApi);
  getCohortAnalysis = this.analyticsApi.getCohortAnalysis.bind(this.analyticsApi);
  getFeatureUsage = this.analyticsApi.getFeatureUsage.bind(this.analyticsApi);
  getTierUsage = this.analyticsApi.getTierUsage.bind(this.analyticsApi);
  getHeavyUsers = this.analyticsApi.getHeavyUsers.bind(this.analyticsApi);
  getMediaGenerationStats = this.analyticsApi.getMediaGenerationStats.bind(this.analyticsApi);
  getHealthMetrics = this.analyticsApi.getHealthMetrics.bind(this.analyticsApi);
  getModelHealth = this.analyticsApi.getModelHealth.bind(this.analyticsApi);
  getInfrastructureMetrics = this.analyticsApi.getInfrastructureMetrics.bind(this.analyticsApi);

  // ============================================================================
  // Billing & Payment API - Delegate to BillingPaymentApi
  // ============================================================================
  
  getPaymentAnalytics = this.billingApi.getPaymentAnalytics.bind(this.billingApi);
  getBillingSnapshot = this.billingApi.getBillingSnapshot.bind(this.billingApi);
  syncBillingData = this.billingApi.syncBillingData.bind(this.billingApi);
  recalculateUserTier = this.billingApi.recalculateUserTier.bind(this.billingApi);
  overrideUserTier = this.billingApi.overrideUserTier.bind(this.billingApi);
  relinkSubscription = this.billingApi.relinkSubscription.bind(this.billingApi);
  linkStripeCustomer = this.billingApi.linkStripeCustomer.bind(this.billingApi);
  getBillingDrift = this.billingApi.getBillingDrift.bind(this.billingApi);
  getPendingCancellations = this.billingApi.getPendingCancellations.bind(this.billingApi);
  getProviderCustomer = this.billingApi.getProviderCustomer.bind(this.billingApi);
  issueRefund = this.billingApi.issueRefund.bind(this.billingApi);
  getUserBillingCredits = this.billingApi.getUserBillingCredits.bind(this.billingApi);
  issueBillingCredit = this.billingApi.issueBillingCredit.bind(this.billingApi);
  consumeBillingCredit = this.billingApi.consumeBillingCredit.bind(this.billingApi);
  refreshCreditBalance = this.billingApi.refreshCreditBalance.bind(this.billingApi);
  getOutstandingCreditLiability = this.billingApi.getOutstandingCreditLiability.bind(this.billingApi);
  cancelSubscriptionWithCredit = this.billingApi.cancelSubscriptionWithCredit.bind(this.billingApi);

  // ============================================================================
  // Admin & Auth API - Delegate to AdminAuthApi
  // ============================================================================
  
  getAlerts = this.adminAuthApi.getAlerts.bind(this.adminAuthApi);
  acknowledgeAlert = this.adminAuthApi.acknowledgeAlert.bind(this.adminAuthApi);
  resolveAlert = this.adminAuthApi.resolveAlert.bind(this.adminAuthApi);
  dismissAlert = this.adminAuthApi.dismissAlert.bind(this.adminAuthApi);
  getAdminUsers = this.adminAuthApi.getAdminUsers.bind(this.adminAuthApi);
  getPendingAdminUsers = this.adminAuthApi.getPendingAdminUsers.bind(this.adminAuthApi);
  promoteToAdmin = this.adminAuthApi.promoteToAdmin.bind(this.adminAuthApi);
  promoteToAdminByEmail = this.adminAuthApi.promoteToAdminByEmail.bind(this.adminAuthApi);
  revokeAdminAccess = this.adminAuthApi.revokeAdminAccess.bind(this.adminAuthApi);
  updateAdminUserRole = this.adminAuthApi.updateAdminUserRole.bind(this.adminAuthApi);
  verifyAdminAccess = this.adminAuthApi.verifyAdminAccess.bind(this.adminAuthApi);
  getAdminProfile = this.adminAuthApi.getAdminProfile.bind(this.adminAuthApi);
  recordLoginActivity = this.adminAuthApi.recordLoginActivity.bind(this.adminAuthApi);
  getOpsUsers = this.adminAuthApi.getOpsUsers.bind(this.adminAuthApi);
  getAdmins = this.adminAuthApi.getAdmins.bind(this.adminAuthApi);
  createAdmin = this.adminAuthApi.createAdmin.bind(this.adminAuthApi);
  getAuditLogs = this.adminAuthApi.getAuditLogs.bind(this.adminAuthApi);
  getCustomRoleConfig = this.adminAuthApi.getCustomRoleConfig.bind(this.adminAuthApi);
  updateCustomRoleConfig = this.adminAuthApi.updateCustomRoleConfig.bind(this.adminAuthApi);
  createCustomRoleConfig = this.adminAuthApi.createCustomRoleConfig.bind(this.adminAuthApi);
  getAllCustomRoleConfigs = this.adminAuthApi.getAllCustomRoleConfigs.bind(this.adminAuthApi);
  resetRoleConfigToDefault = this.adminAuthApi.resetRoleConfigToDefault.bind(this.adminAuthApi);

  // ============================================================================
  // Quota & Tier API - Delegate to QuotaTierApi
  // ============================================================================
  
  getQuotas = this.quotaTierApi.getQuotas.bind(this.quotaTierApi);
  getQuotaForTier = this.quotaTierApi.getQuotaForTier.bind(this.quotaTierApi);
  updateQuotaForTier = this.quotaTierApi.updateQuotaForTier.bind(this.quotaTierApi);
  createQuotaForTier = this.quotaTierApi.createQuotaForTier.bind(this.quotaTierApi);
  deleteQuotaForTier = this.quotaTierApi.deleteQuotaForTier.bind(this.quotaTierApi);
  initializeDefaultQuotas = this.quotaTierApi.initializeDefaultQuotas.bind(this.quotaTierApi);
  diagnoseQuotaValidation = this.quotaTierApi.diagnoseQuotaValidation.bind(this.quotaTierApi);

  // ============================================================================
  // Email & Notification API - Delegate to EmailNotificationApi
  // ============================================================================
  
  previewEmail = this.emailApi.previewEmail.bind(this.emailApi);
  uploadEmailImage = this.emailApi.uploadEmailImage.bind(this.emailApi);
  getEmailRecords = this.emailApi.getEmailRecords.bind(this.emailApi);
  getEmailRecord = this.emailApi.getEmailRecord.bind(this.emailApi);
  cancelEmailRecord = this.emailApi.cancelEmailRecord.bind(this.emailApi);
  getEmailCampaigns = this.emailApi.getEmailCampaigns.bind(this.emailApi);
  getEmailCampaign = this.emailApi.getEmailCampaign.bind(this.emailApi);
  createEmailCampaign = this.emailApi.createEmailCampaign.bind(this.emailApi);
  updateEmailCampaign = this.emailApi.updateEmailCampaign.bind(this.emailApi);
  previewEmailCampaign = this.emailApi.previewEmailCampaign.bind(this.emailApi);
  scheduleEmailCampaign = this.emailApi.scheduleEmailCampaign.bind(this.emailApi);
  sendEmailCampaign = this.emailApi.sendEmailCampaign.bind(this.emailApi);
  cancelEmailCampaign = this.emailApi.cancelEmailCampaign.bind(this.emailApi);
  unscheduleEmailCampaign = this.emailApi.unscheduleEmailCampaign.bind(this.emailApi);
  getEmailCampaignStats = this.emailApi.getEmailCampaignStats.bind(this.emailApi);
  deleteEmailCampaign = this.emailApi.deleteEmailCampaign.bind(this.emailApi);
  resumeEmailCampaign = this.emailApi.resumeEmailCampaign.bind(this.emailApi);
  translateText = this.emailApi.translateText.bind(this.emailApi);
  improveEmailContent = this.emailApi.improveEmailContent.bind(this.emailApi);

  // ============================================================================
  // Organization API - Delegate to OrganizationApi
  // ============================================================================
  
  getOrganizations = this.organizationApi.getOrganizations.bind(this.organizationApi);
  getOrganizationDetails = this.organizationApi.getOrganizationDetails.bind(this.organizationApi);
  suspendTeam = this.organizationApi.suspendTeam.bind(this.organizationApi);
  deleteTeamImmediately = this.organizationApi.deleteTeamImmediately.bind(this.organizationApi);

  // ============================================================================
  // Forum API - Delegate to ForumApi
  // ============================================================================
  
  getForumActivity = this.forumApi.getForumActivity.bind(this.forumApi);
  getForumPosts = this.forumApi.getForumPosts.bind(this.forumApi);
  getForumStats = this.forumApi.getForumStats.bind(this.forumApi);
  pinPost = this.forumApi.pinPost.bind(this.forumApi);
  markPostSolved = this.forumApi.markPostSolved.bind(this.forumApi);
  hidePost = this.forumApi.hidePost.bind(this.forumApi);
  unhidePost = this.forumApi.unhidePost.bind(this.forumApi);
  deletePost = this.forumApi.deletePost.bind(this.forumApi);
  translatePost = this.forumApi.translatePost.bind(this.forumApi);
  tombstoneReply = this.forumApi.tombstoneReply.bind(this.forumApi);
  deleteReply = this.forumApi.deleteReply.bind(this.forumApi);

  // ============================================================================
  // Legacy Methods - Maintained for backward compatibility
  // ============================================================================

  /**
   * Legacy ping method for backward compatibility
   */
  async ping(): Promise<boolean> {
    const health = await this.healthCheck();
    return health.healthy;
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;
